import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { StatTile } from "@/components/dashboard/StatTile";
import { Button } from "@/components/design/Button";
import { Select } from "@/components/design/Select";
import { Switch } from "@/components/design/Switch";
import {
  fetchOverview,
  hasRunningRun,
  TobisNotConfiguredError,
  triggerFetch,
  type TobisOverview,
  type TobisRunPoint,
  type TobisSchedule,
  type TobisSource,
} from "@/lib/tobis";
import { logApplicationFailure } from "@/lib/user-error";
import { TobisBoxOfficeMismatchPanel } from "./TobisBoxOfficeMismatchPanel";
import { TobisPageShell } from "./TobisPageShell";

// Prototyp "tobis": eine Stelle fuer die Ingest-Pipeline — auf einen Blick
// sehen, wie es dem Bestand geht, und von hier aus triggern.
//
// Fehlgeschlagene Laeufe werden neutral dargestellt, nicht rot: Danger ist laut
// UI_CONVENTIONS eine Button-Farbe fuer destruktive Aktionen, keine
// Highlight-Farbe fuer Zustaende. Das Warn-Glyph bei "failed" ist deshalb
// monochrom (ink), der Zustand steht im Text/aria-label, nicht in der Farbe.

const POLL_MS = 3000;

interface SourceSummary {
  source: TobisSource;
  current: number;
  superseded: number;
  lastFetchedAt: string | null;
  // Aus runHistory (letzter Punkt der Pro-Quelle-Serie), NICHT aus dem
  // global gedeckelten Fetch-Log: das zeigt bei seltener laufenden Quellen
  // (tvmaze-catalog, tmdb-us, ...) sonst "kein Lauf", weil Viellaeufer wie
  // tmdb-image-bridge die letzten 20 globalen Eintraege komplett fuellen.
  lastRun: TobisRunPoint | undefined;
  schedule: TobisSchedule | undefined;
  // Gesetzt, wenn diese Quelle ihre Records bewusst im Datentopf einer
  // anderen zaehlt (siehe sourceStoresAs im BFF) - current/superseded sind
  // dann strukturell 0, das ist kein Ausfall.
  storesAs: string | null;
  history: TobisRunPoint[];
}

function summarize(overview: TobisOverview): SourceSummary[] {
  return overview.sources.map((source) => {
    const stats = overview.stats.filter((s) => s.source === source);
    const timestamps = stats.map((s) => s.lastFetchedAt).filter((t): t is string => t !== null);
    const history = overview.runHistory[source] ?? [];
    return {
      source,
      current: stats.reduce((sum, s) => sum + s.current, 0),
      superseded: stats.reduce((sum, s) => sum + s.superseded, 0),
      lastFetchedAt: timestamps.sort().at(-1) ?? null,
      lastRun: history.at(-1),
      schedule: overview.schedules.find((s) => s.source === source),
      storesAs: overview.storesAs[source] ?? null,
      history,
    };
  });
}

// Kleine Balken-Sparkline: Zulauf (recordsNew) der letzten Laeufe dieser
// Quelle, aeltester links. Rein dekorativ (aria-hidden) - die Kernzahlen
// stehen textuell daneben (Sources-Zeilen), die Sparkline zeigt nur den
// Trend. Failed-Laeufe bekommen einen kurzen, kraeftigeren Steg statt einer
// Nullhoehe: sonst sieht ein fehlgeschlagener Lauf optisch identisch aus wie
// "nichts Neues", was der eigentliche Unterschied ist (Fehler vs. leerer
// Erfolg).
function IngestSparkline({ points }: { points: TobisRunPoint[] }) {
  if (points.length === 0) return null;
  const width = 120;
  const height = 22;
  const gap = 2;
  const max = Math.max(1, ...points.map((p) => p.recordsNew));
  const barWidth = (width - gap * (points.length - 1)) / points.length;

  return (
    <svg width={width} height={height} aria-hidden className="shrink-0">
      {points.map((p, i) => {
        const failed = p.status === "failed";
        const h = failed ? 3 : Math.max(2, Math.round((p.recordsNew / max) * height));
        const x = i * (barWidth + gap);
        return (
          <rect
            key={p.startedAt}
            x={x}
            y={height - h}
            width={Math.max(1, barWidth)}
            height={h}
            rx={1}
            className={failed ? "fill-ink/55" : "fill-ink/30"}
          >
            <title>
              {new Date(p.startedAt).toLocaleDateString()} · {p.status} · +{p.recordsNew}
            </title>
          </rect>
        );
      })}
    </svg>
  );
}

// Monochromes Warndreieck fuer "letzter Lauf fehlgeschlagen" - Zustand steht
// im begleitenden Text/title, das Glyph traegt keine eigene Bedeutung ohne
// ihn (siehe Datei-Kommentar zu Danger/Farbe).
function WarningGlyph() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5 shrink-0 text-ink/55"
    >
      <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    </svg>
  );
}

export function TobisAdminPage() {
  const intl = useIntl();
  const [overview, setOverview] = useState<TobisOverview | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "notConfigured" | "error">("loading");
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<TobisSource | null>(null);
  const [logSource, setLogSource] = useState("");
  const [onlyFailed, setOnlyFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setOverview(await fetchOverview());
      setPhase("ready");
    } catch (err) {
      if (err instanceof TobisNotConfiguredError) {
        setPhase("notConfigured");
        return;
      }
      logApplicationFailure("tobis.overview");
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Der Trigger antwortet 202 und laeuft im Hintergrund weiter, deshalb wird
  // nachgezogen, solange ein Lauf offen ist.
  useEffect(() => {
    if (!overview || !hasRunningRun(overview.runs)) return;
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [overview, load]);

  const summaries = useMemo(() => (overview ? summarize(overview) : []), [overview]);

  const kpis = useMemo(() => {
    if (!overview) return null;
    const totalCurrent = summaries.reduce((sum, s) => sum + s.current, 0);
    const failing = summaries.filter((s) => s.lastRun?.status === "failed").length;
    const lastRunAt = overview.runs.map((r) => r.startedAt).sort().at(-1) ?? null;
    const scheduledActive = overview.schedules.filter((s) => s.enabled).length;
    return { totalCurrent, failing, lastRunAt, scheduledActive };
  }, [overview, summaries]);

  const filteredRuns = useMemo(() => {
    if (!overview) return [];
    return overview.runs.filter(
      (r) => (logSource ? r.source === logSource : true) && (onlyFailed ? r.status === "failed" : true),
    );
  }, [overview, logSource, onlyFailed]);

  async function onTrigger(source: TobisSource) {
    setBusy(source);
    setNotice(null);
    const outcome = await triggerFetch(source);
    setBusy(null);

    if (outcome === "started") {
      setNotice(
        intl.formatMessage(
          { id: "tobis.notice.started", defaultMessage: "Fetch for {source} started." },
          { source },
        ),
      );
      await load();
      return;
    }

    setNotice(
      outcome === "alreadyRunning"
        ? intl.formatMessage(
            { id: "tobis.notice.alreadyRunning", defaultMessage: "A fetch for {source} is already running." },
            { source },
          )
        : outcome === "forbidden"
          ? intl.formatMessage({
              id: "tobis.notice.forbidden",
              defaultMessage: "You lack the permission to trigger a fetch.",
            })
          : intl.formatMessage({
              id: "tobis.notice.failed",
              defaultMessage: "The fetch could not be started.",
            }),
    );
  }

  function formatMoment(value: string | null): string {
    if (!value) return intl.formatMessage({ id: "tobis.never", defaultMessage: "never" });
    return intl.formatDate(new Date(value), { dateStyle: "short", timeStyle: "short" });
  }

  // "heute 10:00"/"morgen 10:00" statt vollem Datum: der naechste Tick liegt
  // per Definition nie mehr als einen Tag entfernt (siehe scheduleOverview im
  // BFF), ein Kalenderdatum waere hier nur Rauschen.
  function formatNextRun(iso: string): string {
    const next = new Date(iso);
    const time = intl.formatDate(next, { hour: "2-digit", minute: "2-digit" });
    const sameDay = next.toDateString() === new Date().toDateString();
    return sameDay
      ? intl.formatMessage({ id: "tobis.schedule.today", defaultMessage: "today {time}" }, { time })
      : intl.formatMessage({ id: "tobis.schedule.tomorrow", defaultMessage: "tomorrow {time}" }, { time });
  }

  function statusLabel(status: string): string {
    const known: Record<string, string> = {
      running: intl.formatMessage({ id: "tobis.status.running", defaultMessage: "running" }),
      ok: intl.formatMessage({ id: "tobis.status.ok", defaultMessage: "ok" }),
      partial: intl.formatMessage({ id: "tobis.status.partial", defaultMessage: "partial" }),
      failed: intl.formatMessage({ id: "tobis.status.failed", defaultMessage: "failed" }),
    };
    return known[status] ?? status;
  }

  const sourceOptions = overview
    ? [
        { value: "", label: intl.formatMessage({ id: "tobis.runs.filter.allSources", defaultMessage: "All sources" }) },
        ...overview.sources.map((s) => ({ value: s, label: s })),
      ]
    : [];

  return (
    <TobisPageShell
      eyebrow={intl.formatMessage({ id: "tobis.eyebrow", defaultMessage: "Movie" })}
      title={intl.formatMessage({ id: "tobis.title", defaultMessage: "Data sources" })}
      intro={intl.formatMessage({
        id: "tobis.intro",
        defaultMessage:
          "TVmaze supplies the candidates and reports what changed; the IMDb id it carries is the bridge to OMDb, which adds ratings, votes and box office. Fetches run in the background, the log below updates on its own.",
      })}
    >
      {phase === "notConfigured" || phase === "error" || notice ? (
        <div className="box bg-surface p-6">
          {phase === "notConfigured" && (
            <p className="max-w-xl text-sm text-ink/55">
              {intl.formatMessage({
                id: "tobis.notConfigured",
                defaultMessage:
                  "No database configured. Set TOBIS_DATABASE_URL in the BFF environment and run the migration.",
              })}
            </p>
          )}
          {phase === "error" && (
            <p className="max-w-xl text-sm text-ink/55">
              {intl.formatMessage({
                id: "tobis.loadError",
                defaultMessage: "The overview could not be loaded.",
              })}
            </p>
          )}
          {notice && <p className="max-w-xl text-sm text-ink/70">{notice}</p>}
        </div>
      ) : null}

      {phase === "ready" && (
        <>
          <div className="grid grid-cols-2 gap-3 @sm:grid-cols-4">
            <StatTile
              compact
              label={intl.formatMessage({ id: "tobis.kpi.records", defaultMessage: "Records" })}
              value={intl.formatNumber(kpis?.totalCurrent ?? 0)}
              sub={intl.formatMessage({ id: "tobis.kpi.recordsSub", defaultMessage: "current, all sources" })}
            />
            <StatTile
              compact
              label={intl.formatMessage({ id: "tobis.kpi.failing", defaultMessage: "Sources failing" })}
              value={String(kpis?.failing ?? 0)}
              sub={intl.formatMessage({
                id: "tobis.kpi.failingSub",
                defaultMessage: "of {total} sources",
              }, { total: summaries.length })}
            />
            <StatTile
              compact
              label={intl.formatMessage({ id: "tobis.kpi.lastRun", defaultMessage: "Last run" })}
              value={formatMoment(kpis?.lastRunAt ?? null)}
            />
            <StatTile
              compact
              label={intl.formatMessage({ id: "tobis.kpi.scheduled", defaultMessage: "Scheduled" })}
              value={intl.formatMessage(
                { id: "tobis.kpi.scheduledValue", defaultMessage: "{active}/{total}" },
                { active: kpis?.scheduledActive ?? 0, total: overview?.schedules.length ?? 0 },
              )}
              sub={intl.formatMessage({ id: "tobis.kpi.scheduledSub", defaultMessage: "sources on a timer" })}
            />
          </div>

          <div className="box bg-surface p-6">
            <div className="text-[10px] tracking-[0.25em] text-ink/55">
              {intl.formatMessage({ id: "tobis.sources.title", defaultMessage: "Sources" })}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 @sm:grid-cols-2 @lg:grid-cols-3">
              {summaries.map((s) => (
                <div key={s.source} className="box-flat flex flex-col gap-2 bg-page p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm text-ink">
                        <span className="truncate">{s.source}</span>
                        {s.lastRun?.status === "failed" && (
                          <span
                            title={intl.formatMessage({
                              id: "tobis.sources.failedHint",
                              defaultMessage: "Last run failed",
                            })}
                          >
                            <WarningGlyph />
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[12px] text-ink/40">
                        {s.storesAs
                          ? intl.formatMessage(
                              { id: "tobis.sources.storesAs", defaultMessage: "counts under {target}" },
                              { target: s.storesAs },
                            )
                          : intl.formatMessage(
                              {
                                id: "tobis.sources.counts",
                                defaultMessage:
                                  "{current} current, {superseded} historic · last data {moment}",
                              },
                              {
                                current: s.current,
                                superseded: s.superseded,
                                moment: formatMoment(s.lastFetchedAt),
                              },
                            )}
                      </div>
                      {s.lastRun && (
                        <div className="mt-1 text-[12px] text-ink/40">
                          {intl.formatMessage(
                            {
                              id: "tobis.sources.lastRun",
                              defaultMessage: "last run {status}, {seen} seen, {created} new",
                            },
                            {
                              status: statusLabel(s.lastRun.status),
                              seen: s.lastRun.recordsSeen,
                              created: s.lastRun.recordsNew,
                            },
                          )}
                        </div>
                      )}
                      {s.schedule && (
                        <div className="mt-1 text-[12px] text-ink/40">
                          {s.schedule.enabled
                            ? intl.formatMessage(
                                { id: "tobis.sources.scheduled", defaultMessage: "scheduled · next {when}" },
                                { when: formatNextRun(s.schedule.nextRun) },
                              )
                            : intl.formatMessage({
                                id: "tobis.sources.scheduledOff",
                                defaultMessage: "timer configured, currently off",
                              })}
                        </div>
                      )}
                    </div>
                  </div>
                  {s.history.length > 0 && (
                    <div>
                      <div className="text-[10px] text-ink/40">
                        {intl.formatMessage({ id: "tobis.sources.trend", defaultMessage: "Inflow trend" })}
                      </div>
                      <IngestSparkline points={s.history} />
                    </div>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void onTrigger(s.source)}
                    disabled={busy === s.source || s.lastRun?.status === "running"}
                    className="mt-auto self-start"
                  >
                    {s.lastRun?.status === "running"
                      ? intl.formatMessage({ id: "tobis.status.running", defaultMessage: "running" })
                      : intl.formatMessage({ id: "tobis.trigger", defaultMessage: "Fetch now" })}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="box bg-surface p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-[10px] tracking-[0.25em] text-ink/55">
                {intl.formatMessage({ id: "tobis.runs.title", defaultMessage: "Fetch log" })}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Select
                  value={logSource}
                  onChange={setLogSource}
                  options={sourceOptions}
                  hideEmpty
                  className="w-40"
                />
                <Switch
                  checked={onlyFailed}
                  onChange={setOnlyFailed}
                  ariaLabel={intl.formatMessage({
                    id: "tobis.runs.filter.onlyFailedAria",
                    defaultMessage: "Show only failed runs",
                  })}
                  label={{
                    on: intl.formatMessage({ id: "tobis.runs.filter.failed", defaultMessage: "FAILED" }),
                    off: intl.formatMessage({ id: "tobis.runs.filter.all", defaultMessage: "ALL" }),
                  }}
                />
              </div>
            </div>

            {overview?.runs.length === 0 ? (
              <p className="mt-4 text-sm text-ink/55">
                {intl.formatMessage({ id: "tobis.runs.empty", defaultMessage: "No fetch has run yet." })}
              </p>
            ) : filteredRuns.length === 0 ? (
              <p className="mt-4 text-sm text-ink/55">
                {intl.formatMessage({
                  id: "tobis.runs.emptyFiltered",
                  defaultMessage: "No runs match this filter.",
                })}
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-[13px]">
                  <thead>
                    <tr className="text-[12px] text-ink/45">
                      <th className="py-2 pr-4 font-normal">
                        {intl.formatMessage({ id: "tobis.runs.source", defaultMessage: "Source" })}
                      </th>
                      <th className="py-2 pr-4 font-normal">
                        {intl.formatMessage({ id: "tobis.runs.started", defaultMessage: "Started" })}
                      </th>
                      <th className="py-2 pr-4 font-normal">
                        {intl.formatMessage({ id: "tobis.runs.status", defaultMessage: "Status" })}
                      </th>
                      <th className="py-2 pr-4 text-right font-normal">
                        {intl.formatMessage({ id: "tobis.runs.seen", defaultMessage: "Seen" })}
                      </th>
                      <th className="py-2 pr-4 text-right font-normal">
                        {intl.formatMessage({ id: "tobis.runs.new", defaultMessage: "New" })}
                      </th>
                      <th className="py-2 text-right font-normal">
                        {intl.formatMessage({ id: "tobis.runs.changed", defaultMessage: "Changed" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRuns.map((run) => (
                      <tr key={run.id} className="align-top text-ink/70">
                        <td className="py-2 pr-4">{run.source}</td>
                        <td className="py-2 pr-4">{formatMoment(run.startedAt)}</td>
                        <td className="py-2 pr-4">
                          {statusLabel(run.status)}
                          {/* Serverseitiges Datenfeld des Laufs, keine Exception-Meldung. */}
                          {run.error && <div className="mt-1 text-[12px] text-ink/40">{run.error}</div>}
                        </td>
                        <td className="py-2 pr-4 text-right">{run.recordsSeen}</td>
                        <td className="py-2 pr-4 text-right">{run.recordsNew}</td>
                        <td className="py-2 text-right">{run.recordsChanged}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <TobisBoxOfficeMismatchPanel />
        </>
      )}
    </TobisPageShell>
  );
}
